import React, { useState, useEffect, useRef } from 'react'
import AudioControls from './AudioControls'
import styled, { keyframes, css } from 'styled-components'
import { scroller, animateScroll, Element } from 'react-scroll'

import './styles.css'
import { ReactComponent as Play } from '../../assets/play.svg'
import { ReactComponent as Pause } from '../../assets/pause.svg'

// credit to Ryan Finni https://letsbuildui.dev/articles/building-an-audio-player-with-react-hooks

const Player = ({ tracks }) => {
	// State
	const [trackIndex, setTrackIndex] = useState(0)
	const [trackProgress, setTrackProgress] = useState(0)
	const [isPlaying, setIsPlaying] = useState(false)
	// Destructure for conciseness
	const { title, artist, color, image, audioSrc } = tracks[trackIndex]
	// const [togglePlayPause, setTogglePlayPause] = useState(null);

	// Refs
	const audioRef = useRef(new Audio(audioSrc))
	const intervalRef = useRef()
	const isReady = useRef(false)

	// Destructure for conciseness
	const { duration } = audioRef.current

	const currentPercentage = duration
		? `${(trackProgress / duration) * 100}%`
		: '0%'

	const trackStyling = `
    -webkit-gradient(linear, 0% 0%, 100% 0%, color-stop(${currentPercentage}, var(--third-color-highlight)), color-stop(${currentPercentage}, #fff))
  `

	const startTimer = () => {
		// Clear any timers already running
		clearInterval(intervalRef.current)

		intervalRef.current = setInterval(() => {
			if (audioRef.current.ended) {
				toNextTrack()
			} else {
				setTrackProgress(audioRef.current.currentTime)
			}
		}, [1000])
	}

	const onScrub = (value) => {
		// Clear any timers already running
		clearInterval(intervalRef.current)
		audioRef.current.currentTime = value

		setTrackProgress(audioRef.current.currentTime)
	}

	const onScrubEnd = () => {
		// If not already playing, start
		if (!isPlaying) {
			setIsPlaying(true)
		}
		startTimer()
	}

	const toPrevTrack = () => {
		if (trackProgress <= 5) {
			if (trackIndex - 1 < 0) {
				setTrackIndex(tracks.length - 1)
			} else {
				setTrackIndex(trackIndex - 1)
			}
		} else {
			onScrub(0)
			onScrubEnd()
		}
	}

	const toNextTrack = () => {
		if (trackIndex < tracks.length - 1) {
			setTrackIndex(trackIndex + 1)
		} else {
			setTrackIndex(0)
		}
	}

	useEffect(() => {
		if (isPlaying) {
			audioRef.current.play()
			startTimer()
		} else {
			audioRef.current.pause()
		}
	}, [isPlaying])

	// Handles cleanup and setup when changing tracks
	useEffect(() => {
		audioRef.current.pause()

		audioRef.current = new Audio(audioSrc)
		audioRef.current.volume = 0.45
		setTrackProgress(audioRef.current.currentTime)

		if (isReady.current) {
			audioRef.current.play()
			setIsPlaying(true)
			startTimer()
		} else {
			// Set the isReady ref as true for the next pass
			isReady.current = true
		}
	}, [trackIndex])

	useEffect(() => {
		audioRef.current.volume = 0.45
		// Pause and clean up on unmount
		return () => {
			audioRef.current.pause()
			clearInterval(intervalRef.current)
		}
	}, [])

	const myRef = useRef(null)
	const executeScroll = () => myRef.current.scrollIntoView()
	useEffect(() => {
		if (myRef.current.getAttribute('id') === trackIndex) executeScroll()
	}, [trackIndex])
	if (myRef) {
		console.log({
			myRefcurrent: myRef.current,
			attribue: myRef.current,
		})
	}

	return (
		<Wrapper>
			<div className='audio-player'>
				<div className='track-info'>
					<h2 className='title'>{title}</h2>
					<h3 className='artist'>{artist}</h3>
					<AudioControls
						isPlaying={isPlaying}
						onPrevClick={toPrevTrack}
						onNextClick={toNextTrack}
						onPlayPauseClick={setIsPlaying}
					/>
					<input
						type='range'
						value={trackProgress}
						step='1'
						min='0'
						max={duration ? duration : `${duration}`}
						className='progress'
						onChange={(e) => onScrub(e.target.value)}
						onMouseUp={onScrubEnd}
						onKeyUp={onScrubEnd}
						style={{ background: trackStyling }}
					/>
				</div>
			</div>
			<Songlist>
				<Element id='containerElement'>
					{tracks.map((track, id) => {
						return (
							<Element id={`element${id}`} key={`element${id}`}>
								<SongChoice
									currentlyPlaying={id === trackIndex ? true : false}
									key={track.title}
									onClick={() => {
										id !== trackIndex
											? setTrackIndex(id)
											: setIsPlaying(!isPlaying)
										return
									}}
									ref={myRef}
								>
									<PlayPause>
										{id === trackIndex && isPlaying ? (
											<Pause />
										) : (
											<Play style={{ margin: '0px -1px 0px 2px' }} />
										)}
									</PlayPause>
									{track.title} - {track.artist}
									<StyledLength>{track.length}</StyledLength>
								</SongChoice>
							</Element>
						)
					})}
				</Element>
			</Songlist>
		</Wrapper>
	)
}

const Songlist = styled.ul`
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	flex-grow: 1;
	margin: 2rem;
	height: 330px;
	flex-shrink: 0;
	width: clamp(380px, 40vw, 40vw);
	overflow-y: scroll;

	@media (max-width: 1000px) {
		width: 90%;
		margin: 5% 0;
		flex-shrink: 1;
		height: 220px;
	}
`
const SongChoice = styled.li`
	display: flex;
	align-items: center;
	background: white;
	color: black;
	padding: 1rem;
	transition: 0.3s ease-in-out;
	line-clamp: 2;
	-webkit-line-clamp: 2;
	text-overflow: ellipsis;
	@media (max-width: 1000px) {
		padding: 10px 5px;
	}

	&:hover {
		background: var(--third-color-highlight);
		${({ currentlyPlaying }) =>
			currentlyPlaying && `var(--third-color-highlight)`}
	}

	${({ currentlyPlaying }) =>
		currentlyPlaying &&
		`color: white; background: var(--third-color-highlight)`}
`

const PlayPause = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	justify-self: flex-start;
	margin: 0 0.4rem;
	padding: 0 0.4rem;
	flex-shrink: 0;
	border: 2px solid black;
	border-radius: 9999px;
	height: 36px;
	width: 36px;
	@media (max-width: 1000px) {
		margin: 2px;

		margin-right: 8px;
	}
`
const StyledLength = styled.div`
	margin-left: auto;
	padding: 0 5px;
`

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: space-around;
	background-color: var(--main-color);
	border-radius: 12px;
	-webkit-box-shadow: 2px 5px 12px 1px rgba(0, 0, 0, 0.35);
	box-shadow: 2px 5px 12px 1px rgba(0, 0, 0, 0.35);
`

export default Player
